import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { APP_PATHS } from '@compflash/contracts';
import { useSession } from '../lib/session';

export function AuthPage({ mode }: { mode: 'signin' | 'signup' }): JSX.Element {
  const { signIn, register } = useSession();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'signup') await register(email, password, displayName || undefined);
      else await signIn(email, password);
      navigate('/scan');
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth">
      <form className="panel" onSubmit={(event) => void submit(event)}>
        <h2>{mode === 'signup' ? 'Create your account' : 'Sign in'}</h2>
        {mode === 'signup' && (
          <label className="field">
            <span>Display name (optional)</span>
            <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} autoComplete="nickname" />
          </label>
        )}
        <label className="field">
          <span>Email</span>
          <input type="email" required value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="username" />
        </label>
        <label className="field">
          <span>Password</span>
          <input
            type="password"
            required
            minLength={mode === 'signup' ? 10 : 1}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
          />
          {mode === 'signup' && <span className="hint">At least 10 characters.</span>}
        </label>
        {error && <p className="error">{error}</p>}
        <button className="primary" type="submit" disabled={busy}>
          {busy ? 'Working…' : mode === 'signup' ? 'Create account' : 'Sign in'}
        </button>
        <p className="muted small">
          {mode === 'signup' ? (
            <>
              Already have an account? <Link to={APP_PATHS.signIn}>Sign in</Link>
            </>
          ) : (
            <>
              New here? <Link to="/signup">Create an account</Link> · <Link to={APP_PATHS.forgotPassword}>Forgot password?</Link>
            </>
          )}
        </p>
      </form>
    </div>
  );
}

export function ForgotPasswordPage(): JSX.Element {
  const { client } = useSession();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await client.requestPasswordReset(email);
      setSent(true);
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // The confirmation deliberately does not say whether an account exists. The
  // server will not tell us, and echoing "no such account" here would hand an
  // attacker the user list the API is careful not to leak.
  if (sent) {
    return (
      <div className="auth">
        <div className="panel">
          <h2>Check your email</h2>
          <p className="muted">
            If an account exists for <strong>{email}</strong>, a reset link is on its way. It expires in an hour and
            works once.
          </p>
          <p className="muted small">
            Nothing arrived? Check spam, then <Link to={APP_PATHS.forgotPassword}>try again</Link>.
          </p>
          <p className="muted small">
            <Link to={APP_PATHS.signIn}>Back to sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth">
      <form className="panel" onSubmit={(event) => void submit(event)}>
        <h2>Reset your password</h2>
        <p className="muted small">We&rsquo;ll email you a link to choose a new one.</p>
        <label className="field">
          <span>Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="username"
          />
        </label>
        {error && <p className="error">{error}</p>}
        <button className="primary" type="submit" disabled={busy}>
          {busy ? 'Sending…' : 'Send reset link'}
        </button>
        <p className="muted small">
          <Link to={APP_PATHS.signIn}>Back to sign in</Link>
        </p>
      </form>
    </div>
  );
}

export function ResetPasswordPage(): JSX.Element {
  const { client } = useSession();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (password !== confirm) {
      setError('Those two passwords do not match.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await client.confirmPasswordReset(token, password);
      setDone(true);
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // A missing token means the link was truncated in transit, which mail
  // clients do often enough to be worth naming rather than showing a form
  // that cannot succeed.
  if (!token) {
    return (
      <div className="auth">
        <div className="panel">
          <h2>This link is incomplete</h2>
          <p className="muted">
            Some email clients cut long links in half. Copy the whole link from the message, or request a new one.
          </p>
          <p className="muted small">
            <Link to={APP_PATHS.forgotPassword}>Request a new link</Link>
          </p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="auth">
        <div className="panel">
          <h2>Password changed</h2>
          <p className="muted">
            You have been signed out everywhere else, so any device that had the old password will need the new one.
          </p>
          <p className="muted small">
            <Link to={APP_PATHS.signIn}>Sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth">
      <form className="panel" onSubmit={(event) => void submit(event)}>
        <h2>Choose a new password</h2>
        <label className="field">
          <span>New password</span>
          <input
            type="password"
            required
            minLength={10}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
          />
          <span className="hint">At least 10 characters.</span>
        </label>
        <label className="field">
          <span>Confirm new password</span>
          <input
            type="password"
            required
            minLength={10}
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            autoComplete="new-password"
          />
        </label>
        {error && <p className="error">{error}</p>}
        <button className="primary" type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Set new password'}
        </button>
        <p className="muted small">
          <Link to={APP_PATHS.signIn}>Back to sign in</Link>
        </p>
      </form>
    </div>
  );
}
